import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Newspaper, Users, FileText, Image, Eye, TrendingUp, CircleAlert } from 'lucide-react';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Dashboard = () => {
  const { getAuthHeaders } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/dashboard`,
        getAuthHeaders()
      );
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      icon: Newspaper,
      title: 'Total Articles',
      value: stats?.total_articles || 0,
      color: 'bg-blue-500',
      description: 'Published articles'
    },
    {
      icon: Users,
      title: 'Subscribers',
      value: stats?.total_subscribers || 0,
      color: 'bg-green-500',
      description: 'Active subscribers'
    },
    {
      icon: FileText,
      title: 'E-Papers',
      value: stats?.total_epapers || 0,
      color: 'bg-purple-500',
      description: 'Uploaded e-papers'
    },
    {
      icon: Image,
      title: 'Photo Galleries',
      value: stats?.total_galleries || 0,
      color: 'bg-orange-500',
      description: 'Photo albums'
    },
    {
      icon: Eye,
      title: 'Visitors Today',
      value: stats?.visitors_today || 0,
      color: 'bg-red-500',
      description: 'Daily visitors'
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg p-6">
        <h1 className="text-3xl font-bold mb-2">Welcome to Admin Dashboard</h1>
        <p className="text-red-100">Manage your Tripura Darpan website content</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow duration-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${stat.color}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {stat.value}
                </div>
                <p className="text-sm font-medium text-gray-700 mb-1">{stat.title}</p>
                <p className="text-xs text-gray-500">{stat.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Latest Articles */}
        <Card>
          <CardHeader>
            <CardTitle>Latest Articles</CardTitle>
            <CardDescription>Recently published articles</CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.latest_articles && stats.latest_articles.length > 0 ? (
              <div className="space-y-4">
                {stats.latest_articles.slice(0, 5).map((article) => (
                  <div key={article.id} className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg">
                    <img
                      src={article.image_url}
                      alt={article.title_english}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-sm line-clamp-2">{article.title_bengali}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge variant="outline" className="text-xs">{article.category}</Badge>
                        <span className="text-xs text-gray-500">{article.date}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No articles yet</p>
            )}
          </CardContent>
        </Card>

        {/* Most Read Articles */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5" />
              <span>Most Read Articles</span>
            </CardTitle>
            <CardDescription>Top performing articles</CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.most_read_articles && stats.most_read_articles.length > 0 ? (
              <div className="space-y-4">
                {stats.most_read_articles.slice(0, 5).map((article, index) => (
                  <div key={article.id} className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm line-clamp-1">{article.title_english}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <Eye className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-500">{article.views} views</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No data available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Commonly used features</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a
              href="/admin/articles"
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-red-600 hover:bg-red-50 transition-all duration-200 text-center"
            >
              <Newspaper className="w-8 h-8 mx-auto mb-2 text-red-600" />
              <p className="font-semibold text-sm">Add Article</p>
            </a>
            <a
              href="/admin/epapers"
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-red-600 hover:bg-red-50 transition-all duration-200 text-center"
            >
              <FileText className="w-8 h-8 mx-auto mb-2 text-red-600" />
              <p className="font-semibold text-sm">Upload E-Paper</p>
            </a>
            <a
              href="/admin/galleries"
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-red-600 hover:bg-red-50 transition-all duration-200 text-center"
            >
              <Image className="w-8 h-8 mx-auto mb-2 text-red-600" />
              <p className="font-semibold text-sm">Add Gallery</p>
            </a>
            <a
              href="/admin/breaking-news"
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-red-600 hover:bg-red-50 transition-all duration-200 text-center"
            >
              <CircleAlert className="w-8 h-8 mx-auto mb-2 text-red-600" />
              <p className="font-semibold text-sm">Breaking News</p>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;